/* eslint-disable @typescript-eslint/naming-convention */
import { z, ZodIssueCode } from 'zod'

export interface TPlayer {
    id?: string | null
    name?: string
    shirt_number?: number | '-'
    order?: number | null
    starter?: boolean
    captain?: boolean
}

export interface FixtureLineup {
    team2_lineup: TPlayer[]
    team2_formation: string | null
    team2_id?: string | null

    team1_id?: string | null
    team1_lineup: TPlayer[]
    team1_formation: string | null

    possible_lineups: boolean
    fixture_id: string | null
    status: number | null
}

export type TTeamLineupData = {
    formation: string | null
    lineup: TPlayer[][]
    substitutions: TPlayer[]
}

export interface CurrentActivePlayer {
    id?: TPlayer['id']
    name?: TPlayer['name']
    shirt_number?: TPlayer['shirt_number']
    order?: TPlayer['order']
    starter?: TPlayer['starter']
    captain?: TPlayer['captain']
    rowIndex?: number
    colIndex?: number
    index?: number
    reason?: number
}

export type NewLineup = (TPlayer | null)[][]
export type NewSubs = TPlayer[] | any[]

export const MatchEventEditorEventType = {
    goal: 30,
    yellow_card: 40,
    yellowred_card: 45,
    red_card: 50,
    substitution: 60,
    // injury_time: 90,
    // free_kick: 150,
    // goal_kick: 151,
    // throw_in: 152,
    // offside: 153,
    corner: 154,
    shot_on_target: 155,
    shot_off_target: 156,
    // goalkeeper_save: 157,
    // injury: 158,
    // penalty_awarded: 161,
    shot_blocked: 172,
    penalty_missed: 666,
    penalty_shootout_event_deep_coverage: 1002,
    // penalty_shootout_starting_team: 1104,
    video_assistant_referee: 2064,
    video_assistant_referee_over: 2065,
    fixture_status: 1013,
} as const

export const MatchEventEditorEventTypeEnum = z.nativeEnum(MatchEventEditorEventType)

export type TMatchEventEditorEventType = z.infer<typeof MatchEventEditorEventTypeEnum>

export const MatchEventEditorGoalExtraInfo = {
    free_kick: -200,
    own_goal: 2,
    penalty: 1,
    header: 3,
    shot: -100,
} as const

export const MatchEventEditorGoalExtraInfoEnum = z.nativeEnum(MatchEventEditorGoalExtraInfo)

export type TMatchEventEditorGoalExtraInfo = z.infer<typeof MatchEventEditorGoalExtraInfoEnum>

// Mirror Consolidated.Enums.Fixture.* in sports.scores.models (STATS-6104). Keep integer values in sync.
// Legacy card extra_info uses the same integer values 1:1 (pre–STATS-6110 API bridge).
export const MatchEventEditorCardLocation = {
    on_field: 0,
    bench: 1,
    manager: 2,
    other_staff: 3,
} as const

export const MatchEventEditorCardLocationEnum = z.nativeEnum(MatchEventEditorCardLocation)

export type TMatchEventEditorCardLocation = z.infer<typeof MatchEventEditorCardLocationEnum>

export const MatchEventEditorCardReason = {
    handball: 0,
    six_seconds_violation: 1,
    back_pass: 2,
    time_wasting: 3,
    rescinded_card: 4,
    not_retreating: 5,
    serious_foul: 6,
    foul: 7,
    excessive_celebration: 8,
    violent_conduct: 9,
    other_reason: 10,
    unsporting_behaviour: 11,
    referee_abuse: 12,
    argument: 13,
    simulation: 14,
    injury: 15,
    crowd_interaction: 16,
    foul_and_abusive_language: 17,
    encroachment: 18,
    persistent_infringement: 19,
    dangerous_play: 20,
    professional_foul_last_man: 21,
    dissent: 22,
    not_visible: 23,
    off_the_ball_foul: 24,
    entering_field: 25,
    leaving_field: 26,
    professional_foul_handball: 27,
    spitting: 28,
    entering_referee_review_area: 29,
    entering_video_operations_room: 30,
    excessive_usage_of_var_signal: 31,
} as const

export const MatchEventEditorCardReasonEnum = z.nativeEnum(MatchEventEditorCardReason)

export type TMatchEventEditorCardReason = z.infer<typeof MatchEventEditorCardReasonEnum>

export const MatchEventEditorPenaltyMissedReason = {
    off_target: 0,
    hit_post: 1,
    saved: 2,
} as const

export const MatchEventEditorPenaltyMissedReasonEnum = z.nativeEnum(MatchEventEditorPenaltyMissedReason)

export type TMatchEventEditorPenaltyMissedReason = z.infer<typeof MatchEventEditorPenaltyMissedReasonEnum>

export const MatchEventEditorSubstitutionReason = {
    tactical: 0,
    injury: 1,
} as const

export const MatchEventEditorSubstitutionReasonEnum = z.nativeEnum(MatchEventEditorSubstitutionReason)

export type TMatchEventEditorSubstitutionReason = z.infer<typeof MatchEventEditorSubstitutionReasonEnum>

export const CARD_EVENT_TYPES = new Set<TMatchEventEditorEventType>([
    MatchEventEditorEventType.yellow_card,
    MatchEventEditorEventType.yellowred_card,
    MatchEventEditorEventType.red_card,
])

export const PENALTY_MISSED_EVENT_TYPES = new Set<TMatchEventEditorEventType>([
    MatchEventEditorEventType.penalty_missed,
    MatchEventEditorEventType.penalty_shootout_event_deep_coverage,
])

export const MatchEventEditorVarCheckExtraInfo = {
    goal: 0,
    penalty: 1,
    red_card: 2,
} as const

export const MatchEventEditorVarCheckExtraInfoEnum = z.nativeEnum(MatchEventEditorVarCheckExtraInfo)

export type TMatchEventEditorVarCheckExtraInfo = z.infer<typeof MatchEventEditorVarCheckExtraInfoEnum>

export const MatchEventEditorVarOverExtraInfo = {
    goal: 0,
    no_goal: 1,
    penalty: 2,
    no_penalty: 3,
    red_card: 4,
    no_red_card: 5,
} as const

export const MatchEventEditorVarOverExtraInfoEnum = z.nativeEnum(MatchEventEditorVarOverExtraInfo)

export type TMatchEventEditorVarOverExtraInfo = z.infer<typeof MatchEventEditorVarOverExtraInfoEnum>

export const MatchEventEditorFixtureExtraInfo = {
    start_1st_half: 6,
    half_time: 31,
    start_2nd_half: 7,
    finish_after_regular_time: 100,
    awaiting_extra_time: 32,
    start_1st_half_extra_time: 41,
    half_time_extra_time: 33,
    start_2nd_half_extra_time: 42,
    finish_after_extra_time: 110,
    awaiting_penalty_shootout: 34,
    start_penalty_shootout: 50,
    finish_after_penalty_shootout: 120,
    match_suspended: 90,
} as const

export const MatchEventEditorFixtureExtraInfoEnum = z.nativeEnum(MatchEventEditorFixtureExtraInfo)

export type TMatchEventEditorFixtureExtraInfo = z.infer<typeof MatchEventEditorFixtureExtraInfoEnum>

export const MatchEventEditorEventModificationType = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
} as const

export type TMatchEventEditorEventModificationType =
    (typeof MatchEventEditorEventModificationType)[keyof typeof MatchEventEditorEventModificationType]

export const MatchEventEditorUnsupportedOperations = {
    CREATE: 0,
    EDIT: 1,
    DELETE: 2,
} as const

export type TMatchEventEditorUnsupportedOperations =
    (typeof MatchEventEditorUnsupportedOperations)[keyof typeof MatchEventEditorUnsupportedOperations]

export type MatchEventEditorEventMatchScore = {
    team1: number
    team2: number
}

export const MatchEventEditorEventFeedProvider = {
    LIVESCOUT: 'livescout',
    OPTA: 'opta',
    SUPEROLOGY_SOCCER: 'superology_soccer',
}

export type TMatchEventEditorEventFeedProvider =
    (typeof MatchEventEditorEventFeedProvider)[keyof typeof MatchEventEditorEventFeedProvider]

export type MatchEventEditorVarReviewDetails = {
    original_decision: TMatchEventEditorVarOverExtraInfo
    outcome: TMatchEventEditorVarOverExtraInfo
    reviewed_event_id?: string
}

export type MatchEventEditorApiEvent = {
    type: TMatchEventEditorEventType
    time: string
    team_id: string
    player1_id: string
    player2_id: string
    modification_type: TMatchEventEditorEventModificationType | null
    event_time: string
    timestamp: string
    score?: MatchEventEditorEventMatchScore
    info: string
    id: number
    feed_provider: TMatchEventEditorEventFeedProvider
    extra_info: number
    date_modified: string
    team1_score?: number
    team2_score?: number
    period_number: number
    player1_name?: string
    player2_name?: string
    review_details: MatchEventEditorVarReviewDetails
    unsupported_operations: TMatchEventEditorUnsupportedOperations[]
    penalty_order: number
    card_reason?: number | null
    penalty_missed_reason?: number | null
    substitution_reason?: number | null
    card_location?: number | null
}

export type EnrichedMatchEditorApiEvent = MatchEventEditorApiEvent & {
    team_name?: string
    isEditable: boolean
    isRemovable: boolean
}

export type MatchEventEditorEvent = {
    stime: string
    side: number
    remaining_time_period?: unknown
    possible_event_match_time?: unknown
    pos_y?: unknown
    pos_x?: unknown
    period_number?: unknown
} & MatchEventEditorApiEvent

export type MatchEventEditorTeamInfo = {
    sport_id: number
    short_code: {
        value: string
    }
    name: string
    id: string
    country_code: {
        value: string
    }
    features: number[]
}

export const MatchStatus = {
    // Live
    started: 20,
    a1st_half: 6,
    a2nd_half: 7,
    halftime: 31,
    awaiting_extra_time: 32,
    extra_time_halftime: 33,
    awaiting_penalties: 34,
    a1st_extra: 41,
    a2nd_extra: 42,
    penalties: 50,
    overtime: 40,
    // Other
    postponed: 60,
    cancelled: 70,
    interrupted: 80,
    coverage_cancelled: 81,
} as const

export const MatchStatusEnum = z.nativeEnum(MatchStatus)

export type TMatchStatus = z.infer<typeof MatchStatusEnum>

export const MatchEventPeriodNumber = {
    first_half: 1,
    second_half: 2,
    first_half_extra_time: 3,
    second_half_extra_time: 4,
    penalties: 5,
} as const

export const MatchEventPeriodNumberEnum = z.nativeEnum(MatchEventPeriodNumber)

export type TMatchEventPeriodNumber = z.infer<typeof MatchEventPeriodNumberEnum>

export type MatchEventEditorGetDataResponse = {
    id: string
    team1_id: string
    team2_id: string
    team1_info?: MatchEventEditorTeamInfo
    team2_info?: MatchEventEditorTeamInfo
    status: TMatchStatus
    date: string
    goals: MatchEventEditorApiEvent[]
    substitutions: MatchEventEditorApiEvent[]
    yellow_cards: MatchEventEditorApiEvent[]
    yellow_red_cards: MatchEventEditorApiEvent[]
    red_cards: MatchEventEditorApiEvent[]
    var_outcomes: MatchEventEditorApiEvent[]
    events: MatchEventEditorApiEvent[]
}

export type MinuteRange = { min: number; max: number }

export const timeRangePerEventPeriod: Record<TMatchEventPeriodNumber, MinuteRange> = {
    [MatchEventPeriodNumber.first_half]: { min: 0, max: 45 },
    [MatchEventPeriodNumber.second_half]: { min: 45, max: 90 },
    [MatchEventPeriodNumber.first_half_extra_time]: { min: 90, max: 105 },
    [MatchEventPeriodNumber.second_half_extra_time]: { min: 105, max: 120 },
    [MatchEventPeriodNumber.penalties]: { min: 90, max: 120 },
}

export const EVENT_TYPES_WITH_SCORE = new Set<TMatchEventEditorEventType>([
    MatchEventEditorEventType.goal,
    MatchEventEditorEventType.penalty_missed,
    MatchEventEditorEventType.penalty_shootout_event_deep_coverage,
])

export const PERIOD_EXTRA_INFO_WITH_SCORE = new Set<number>([
    MatchEventEditorFixtureExtraInfo.half_time,
    MatchEventEditorFixtureExtraInfo.finish_after_regular_time,
    MatchEventEditorFixtureExtraInfo.finish_after_extra_time,
    MatchEventEditorFixtureExtraInfo.finish_after_penalty_shootout,
])

export const EVENT_TYPES_WITH_PENALTY_ORDER = new Set<number>([
    MatchEventEditorEventType.penalty_shootout_event_deep_coverage,
    MatchEventEditorEventType.goal,
    MatchEventEditorEventType.red_card,
    MatchEventEditorEventType.yellow_card,
    MatchEventEditorEventType.yellowred_card,
    MatchEventEditorEventType.substitution,
    MatchEventEditorEventType.video_assistant_referee_over,
])

const Option = z.object({
    value: z.coerce.string(),
    label: z.coerce.string(),
})

const ZMatchTime = z.object({
    minutes: z.coerce.number(),
    seconds: z.coerce
        .number()
        .min(0, 'Valid seconds value must be provided')
        .max(59, 'Valid seconds value must be provided'),
    additionalMinutes: z.coerce.string(),
})

export type MatchTime = z.infer<typeof ZMatchTime>

const RequiredOption = Option.refine(o => o?.value !== undefined && o?.value !== null && o?.value !== '', {
    message: 'Selection is required',
})

const OptionalOption = Option.optional()

const OptionalNumber = z.coerce.number().optional().default(0)

export const EventSchema = z
    .object({
        player1_id: OptionalOption,
        player2_id: OptionalOption,

        team_id: OptionalOption,

        time: ZMatchTime,

        type: RequiredOption.refine(({ value }) => value !== undefined, { message: 'Event type must be selected' }),

        period_number: RequiredOption.refine(({ value }) => value !== undefined, {
            message: 'Period must be selected',
        }),

        extra_info: z.coerce.number().optional(),

        team1_score: OptionalNumber,
        team2_score: OptionalNumber,

        review_original_decision: OptionalOption,
        review_outcome: OptionalOption,
        penalty_order: z.coerce.number().optional(),

        player1_name: z.string().optional(),
        player2_name: z.string().optional(),

        // Reason fields use null as the cleared value — undefined would turn the
        // controlled Select/RadioGroup inputs uncontrolled and leave stale UI state
        card_reason: OptionalOption.nullable(),
        penalty_missed_reason: z.coerce.number().nullable().optional(),
        substitution_reason: z.coerce.number().nullable().optional(),
        card_location: z.coerce.number().nullable().optional(),
    })
    .superRefine((data, ctx) => {
        const {
            type,
            team_id,
            team1_score,
            team2_score,
            review_original_decision,
            review_outcome,
            period_number,
            time,
            penalty_order,
        } = data

        const eventType = Number(type.value)

        /* ---------- Team required (except fixture status) ---------- */
        if (eventType !== MatchEventEditorEventType.fixture_status && !team_id?.value) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                message: 'Team must be selected',
                path: ['team_id'],
            })
        }

        /* ---------- VAR check cannot be created/edited ---------- */
        if (eventType === MatchEventEditorEventType.video_assistant_referee) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                message: 'VAR Check cannot be created or edited',
                path: ['type'],
            })
        }

        /* ---------- VAR over requires review details ---------- */
        if (eventType === MatchEventEditorEventType.video_assistant_referee_over) {
            if (!review_original_decision?.value) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    message: 'Original decision is required',
                    path: ['review_original_decision'],
                })
            }

            if (!review_outcome?.value) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    message: 'Review outcome is required',
                    path: ['review_outcome'],
                })
            }
        }

        /* ---------- Goal must update score ---------- */
        if (eventType === MatchEventEditorEventType.goal) {
            if (team1_score === 0 && team2_score === 0) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    message: 'Score must be updated for a goal',
                    path: ['team1_score'],
                })

                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    message: 'Score must be updated for a goal',
                    path: ['team2_score'],
                })
            }
        }

        /* ---------- Time must be in the period range ---------- */
        if (time.minutes && period_number?.value) {
            const validRange: MinuteRange = timeRangePerEventPeriod[Number(period_number.value)]
            if (time.minutes < validRange.min || time.minutes > validRange.max) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    message: `Minutes should be valid for the current period (${validRange.min} - ${validRange.max})`,
                    path: ['time.minutes'],
                })
            }
        }

        /* ---------- Penalty No. is required if is a penalty shootout goal
                      or is a penalty missed in shootout ---------- */
        if (
            period_number?.value &&
            Number(period_number.value) === MatchEventPeriodNumber.penalties &&
            EVENT_TYPES_WITH_PENALTY_ORDER.has(eventType) &&
            !penalty_order
        ) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                message: 'Penalty number needs to be specified for ordering',
                path: ['penalty_order'],
            })
        }
    })

export type EventFormData = z.infer<typeof EventSchema> & {
    id?: string
    timestamp?: string
}

export type MatchEventEditorEventReasonFields = {
    card_reason?: number | null
    penalty_missed_reason?: number | null
    substitution_reason?: number | null
    card_location?: number | null
}

export type MatchEventEditorPostDataRequest = {
    player1_id?: string | null
    player2_id?: string | null
    player1_name?: string | null
    player2_name?: string | null
    team_id?: string | null
    time: string
    timestamp: string
    type: number
    extra_info?: number | null
    team1_score?: number | null
    team2_score?: number | null
    period_number: number
    review_details?: {
        original_decision?: number | null
        outcome?: number | null
        reviewed_event_id?: string | null
    } | null
} & MatchEventEditorEventReasonFields

export type MatchEventEditorPostDataResponse = {
    fixture_id: string
    event: MatchEventEditorEvent
}

export type MatchEventEditorPutDataRequest = MatchEventEditorPostDataRequest & {
    id: string
}

export type MatchEventEditorPutDataResponse = {
    fixture_id: string
    event: MatchEventEditorEvent
}

export type MatchEventEditorDeleteDataResponse = {
    fixture_id: string
    event: MatchEventEditorEvent
}

export type TeamLineup = {
    captain: boolean
    formation_place: number
    name: string
    player_id: string
    position: number
    position_side: string
    shirt_number: number
    substitute: boolean
}

export type MatchLineup = {
    id: string
    team1_lineup: TeamLineup[]
    team2_lineup: TeamLineup[]
}

export type MatchLineupData = {
    team1Lineup: TeamLineup[]
    team2Lineup: TeamLineup[]
}

export type MatchLineupDataResponse = {
    id?: string
    lineups: MatchLineup
}

export const ApiErrorMessage = {
    UNAUTHORIZED: 'Unauthorized',
}

export const ApiErrorMessageEnum = z.nativeEnum(ApiErrorMessage)

export type TApiErrorMessage = z.infer<typeof ApiErrorMessageEnum>

export const editorsList = {
    LINEUP_EDITOR: 'lineups',
    MATCH_EDITOR: 'match',
} as const

export type TEditorsList = (typeof editorsList)[keyof typeof editorsList]

export const StateEnum = {
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
} as const

export type TStateEnum = (typeof StateEnum)[keyof typeof StateEnum]

export const ReasonEnum = {
    NO_SOURCE: 'no_source',
    LATE_SOURCE: 'late_source',
    BUG: 'bug',
    NO_CAPACITY: 'no_capacity',
} as const

export type TReasonEnum = (typeof ReasonEnum)[keyof typeof ReasonEnum]

export const ModifiedInEnum = {
    PRE_MATCH: 'before_start',
    LIVE: 'live',
    POST_MATCH: 'after_end',
} as const

export type TModifiedInEnum = (typeof ModifiedInEnum)[keyof typeof ModifiedInEnum]

export const LockingActionTypeEnum = {
    LOCK: 'lock',
    UNLOCK: 'unlock',
} as const

export type LockingActionType = (typeof LockingActionTypeEnum)[keyof typeof LockingActionTypeEnum]

export const MatchEditorSelectedTabEnum = {
    EVENTS: 'e',
    MEDIA: 'm',
}

export type TMatchEditorSelectedTab = (typeof MatchEditorSelectedTabEnum)[keyof typeof MatchEditorSelectedTabEnum]

export type LockingAction = {
    type: LockingActionType
    match_time: string
    period_number: number
}

export type MetaData = {
    state?: TStateEnum | null
    reason?: TReasonEnum | null
    comment?: string | null
    modified_in?: TModifiedInEnum | null
    date_modified?: string | null
    locking_action?: LockingActionType | null
    locking_actions?: LockingAction[] | null
}

export type CompetitionListMatch = {
    competition_name: string
    match_name: string
    fixture_id: string
    match_time: string
    meta_data: {
        reason?: TReasonEnum
        state?: TStateEnum
        comment?: string
    }
}

export const MissingPlayerReason = {
    MISSINGPLAYERREASON_UNKNOWN: 0,
    MISSINGPLAYERREASON_INJURY: 1,
    MISSINGPLAYERREASON_SUSPENSION: 2,
    MISSINGPLAYERREASON_DOUBTFUL: 3,
} as const

export const MissingPlayerReasonAreaIds = {
    [MissingPlayerReason.MISSINGPLAYERREASON_INJURY]: 'injured-players',
    [MissingPlayerReason.MISSINGPLAYERREASON_SUSPENSION]: 'suspended-players',
    [MissingPlayerReason.MISSINGPLAYERREASON_DOUBTFUL]: 'doubtful-players',
}

export type TMissingPlayerReason = (typeof MissingPlayerReason)[keyof typeof MissingPlayerReason]

export type TMissingPlayerReasonAreaIds = (typeof MissingPlayerReasonAreaIds)[keyof typeof MissingPlayerReasonAreaIds]

export type MissingPlayer = {
    type: number
    source: string | null
    reason: TMissingPlayerReason
    id: string | null
    name: string
    shirt_number: number | '-'
    captain: boolean
}

export type MissingPlayersList = {
    [key in TMissingPlayerReason]?: MissingPlayer[]
}

type MissingPlayerPostDataRequest = {
    id: null | string
    name: string
    shirt_number: string
    type: number
    source: 'provider' | 'manual'
}

export type MissingPlayersPostDataRequest = {
    fixture_id: string
    team1_id: string
    team2_id: string
    team1_injuries: MissingPlayerPostDataRequest[]
    deleted_team1_injuries: string[]
    team1_suspensions: MissingPlayerPostDataRequest[]
    deleted_team1_suspensions: string[]
    team1_doubtful: MissingPlayerPostDataRequest[]
    deleted_team1_doubtful: string[]
    team2_injuries: MissingPlayerPostDataRequest[]
    deleted_team2_injuries: string[]
    team2_suspensions: MissingPlayerPostDataRequest[]
    deleted_team2_suspensions: string[]
    team2_doubtful: MissingPlayerPostDataRequest[]
    deleted_team2_doubtful: string[]
}

type BaseInfo = {
    country_code: string | null
    id: string | null
    name: string | null
}

export type MatchDetails = {
    category: BaseInfo | null
    competition: BaseInfo | null
    date: string | null
    features: number[] // add feature types here
    id: string
    lineups_editor_meta: MetaData | null
    events_editor_meta: MetaData | null
    status: number | null
    season: BaseInfo | null
    team1: BaseInfo | null
    team2: BaseInfo | null
    media_contents: TMediaContent[]
}

export type TMediaContent = {
    date_modified?: string
    id: string | null
    notify: boolean
    provider_type?: string
    type: string
    url: string
    video_hidden: boolean
}

export type TPlayerData = TPlayer | MissingPlayer

export type AddPlayerFormData = {
    id: string | null
    name?: string
    surname: string
    shirt_number?: number | '-'
    captain: boolean
    playerDetailUrl?: string | null
}

type PositionData = {
    colIndex?: number
    rowIndex?: number
    index?: number
}

export type PlayerPosition = {
    player: TPlayerData
    position?: PositionData
}
